import { IFrequentlyBoughtProduct, ISearchProduct } from '@monofront/types';
import { useEffect, useMemo, useState } from 'react';
import { IAuthState } from '../contexts';
import { UserService } from '../services';
import { Hit } from 'instantsearch.js';

interface IUseFrequentlyBoughtProductsReturn {
  customerFilter: string;
  customSort: (hits: ISearchProduct[]) => Hit<ISearchProduct>[];
  frequentlyBoughtProducts: IFrequentlyBoughtProduct[];
}

interface IUseFrequentlyBoughtProductsParams {
  filterUnavailableProducts?: boolean;
  limit?: number;
}

/**
 * Factory function that build a useFrequentlyBoughtProducts hook
 * @param useAuthentication the hook that handles the authentication
 * @param userService the hook that gets the user informations
 * @returns a hook that enables to get the user's frequently purchased products
 */
export const createUseFrequentlyBoughtProducts = (
  useAuthentication: () => Partial<IAuthState>,
  userService: UserService,
) => {
  return ({
    filterUnavailableProducts = true,
    limit,
  }: IUseFrequentlyBoughtProductsParams): IUseFrequentlyBoughtProductsReturn => {
    const { authUser } = useAuthentication();
    const [frequentlyBoughtProducts, setFrequentlyBoughtProducts] = useState<
      IFrequentlyBoughtProduct[]
    >([]);

    const fetchUserFrequentlyBoughtProducts = async () => {
      if (!authUser?.uid) {
        return;
      }
      const products = await userService.getUserFrequentlyBoughtProducts(
        authUser.uid,
      );

      setFrequentlyBoughtProducts(products.slice(0, limit) || []);
    };

    const customSort = (hits: ISearchProduct[]): Hit<ISearchProduct>[] => {
      if (!frequentlyBoughtProducts) {
        return [];
      }

      const formattedProductsBySku = hits.reduce(
        (accumulator, currentValue) => ({
          ...accumulator,
          [currentValue.sku]: currentValue,
        }),
        {},
      );

      let result = frequentlyBoughtProducts
        .map((product) => formattedProductsBySku[product.sku])
        .filter(Boolean);

      if (filterUnavailableProducts) {
        result = result.filter(
          (product) =>
            formattedProductsBySku[product.sku]?.inventory_quantity > 0,
        );
      }

      return result as Hit<ISearchProduct>[];
    };

    useEffect(() => {
      fetchUserFrequentlyBoughtProducts();
    }, [authUser]);

    const frequentlyBoughtProductsSku = useMemo(
      () => frequentlyBoughtProducts?.map((doc) => doc.sku),
      [frequentlyBoughtProducts],
    );

    return {
      customerFilter: `sku:${frequentlyBoughtProductsSku?.join(' OR sku:')}`,
      customSort,
      frequentlyBoughtProducts,
    };
  };
};
